import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, X, Trash2, Edit3, Play, Youtube, Search, Square, Archive, AlertTriangle, QrCode, DoorOpen, Printer, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { EVENT_TYPES, eventType } from "@/lib/constants";
import PrerollEditor from "@/pages/admin/PrerollEditor";
import { WallpaperPicker } from "@/pages/admin/WallpaperLibrary";
import CinemaDatePicker from "@/components/CinemaDatePicker";
import { fmtDateEU } from "@/lib/dt";
import DisplayMusic from "@/components/DisplayMusic";

const STATUS = [
  { v: "geplant", l: "Geplant" },
  { v: "einlass_bald", l: "Einlass in Kürze" },
  { v: "einlass", l: "Einlass läuft" },
  { v: "laeuft", l: "Vorstellung läuft" },
  { v: "pause", l: "Pause" },
  { v: "beendet", l: "Beendet" },
  { v: "storniert", l: "Storniert" },
];

const FOYER_PRESETS = [
  { key: "zimmer", icon: "🎬", label: "Zimmer · Kino-Klassiker", url: "https://www.youtube.com/watch?v=RxabLA7UQ9k|https://www.youtube.com/watch?v=UDVtMYqUAyw|https://www.youtube.com/watch?v=xLIYNjKz4os|https://www.youtube.com/watch?v=wnFljVWO1B4|https://www.youtube.com/watch?v=Yr59RRuHYD8" },
  { key: "jazz",   icon: "🎷", label: "Jazz-Foyer",        url: "https://www.youtube.com/watch?v=Dx5qFachd3A" },
  { key: "score",  icon: "🎬", label: "Filmmusik-Klassik", url: "https://www.youtube.com/watch?v=g7RTGx9M7wc" },
  { key: "chill",  icon: "🌙", label: "Chill Ambient",     url: "https://www.youtube.com/watch?v=jfKfPfyJRdk" },
];

// Kuratierte Spotify-Playlists nach Kino-Stimmung — genau wie in Filmreihen
const SPOTIFY_MOODS = [
  { name: "Epic · Saga · Fantasy", emoji: "🗡️", playlists: [
    { key: "howard-shore", label: "Herr der Ringe · Score", url: "https://open.spotify.com/album/1BCTOo7BdcAj2mvGtsRMc0" },
    { key: "epic-orchestral", label: "Epic Orchestral", url: "https://open.spotify.com/playlist/37i9dQZF1DWZjqoI9tHFwG" },
    { key: "star-wars", label: "Star Wars · John Williams", url: "https://open.spotify.com/artist/1acQlS8lqAJPxu4bMbqXCk" },
    { key: "avengers", label: "Marvel · Avengers", url: "https://open.spotify.com/playlist/37i9dQZF1DX2VbwlgLLopO" },
  ]},
  { name: "Horror · Thriller · Dark", emoji: "🖤", playlists: [
    { key: "horror-score", label: "Horror Movie Scores", url: "https://open.spotify.com/playlist/37i9dQZF1DX8FwnYE6PRcL" },
    { key: "dark-ambient", label: "Dark Ambient", url: "https://open.spotify.com/playlist/37i9dQZF1DX2pSTOxoPbx9" },
    { key: "halloween", label: "Halloween Party", url: "https://open.spotify.com/playlist/37i9dQZF1DWUDcbsuBhKb0" },
  ]},
  { name: "Romance · Noir · Sensual", emoji: "🌹", playlists: [
    { key: "fifty-shades", label: "Fifty Shades OST", url: "https://open.spotify.com/album/47OKm7ZLTPhWMYaNvxJPWc" },
    { key: "romance", label: "Romantic Cinema", url: "https://open.spotify.com/playlist/37i9dQZF1DWXbLOeOIhbc5" },
    { key: "jazz-noir", label: "Jazz Noir", url: "https://open.spotify.com/playlist/37i9dQZF1DXbITWG1ZJKYt" },
    { key: "chill-lounge", label: "Cinema Lounge", url: "https://open.spotify.com/playlist/37i9dQZF1DX4WYpdgoIcn6" },
  ]},
  { name: "Family · Disney · Animation", emoji: "🎈", playlists: [
    { key: "disney", label: "Disney Hits", url: "https://open.spotify.com/playlist/37i9dQZF1DX8C9xQcOrE6T" },
    { key: "pixar", label: "Pixar Soundtracks", url: "https://open.spotify.com/playlist/37i9dQZF1DX6xOPeSOGone" },
  ]},
  { name: "Sci-Fi · Retro · 80s", emoji: "🚀", playlists: [
    { key: "synthwave", label: "Synthwave / Retrowave", url: "https://open.spotify.com/playlist/37i9dQZF1DX0r3x8OtiwEM" },
    { key: "80s-movies", label: "80s Movie Nights", url: "https://open.spotify.com/playlist/37i9dQZF1DX4o1oenSJRJd" },
    { key: "blade-runner", label: "Sci-Fi Atmospheres", url: "https://open.spotify.com/playlist/37i9dQZF1DXcdQrCbG0OKB" },
  ]},
  { name: "Christmas · Festive", emoji: "🎄", playlists: [
    { key: "christmas", label: "Christmas Hits", url: "https://open.spotify.com/playlist/37i9dQZF1DX0Yxoavh5qJV" },
    { key: "christmas-jazz", label: "Christmas Jazz", url: "https://open.spotify.com/playlist/37i9dQZF1DX4ANONkOtcRy" },
  ]},
];

// Erkennt Spotify-URL — akzeptiert alle Query-Params (si, utm_source, pi, ...)
const isSpotifyUrl = (u) => /open\.spotify\.com\/(?:intl-\w+\/)?(playlist|album|track|artist|episode|show)\/[\w]+/i.test(u || "") || /spotify:(?:playlist|album|track|artist)/i.test(u || "");

const WALL_PRESETS = [
  { key: "wall-logo",     label: "Logo-Wand",             url: "https://customer-assets-0z36b82j.emergentagent.net/job_home-cinema-system/artifacts/68aqecmt_44975CE4-C191-4BB1-BFDC-D83A0E77E5F8.png" },
  { key: "wall-smoke",    label: "Rauch & Glut",          url: "https://customer-assets-0z36b82j.emergentagent.net/job_home-cinema-system/artifacts/8c1jef7h_C43BDB5C-A41E-42D1-8E4A-041A69C21B83.png" },
  { key: "wall-filmroll", label: "Filmrolle · Klassiker", url: "https://customer-assets-0z36b82j.emergentagent.net/job_home-cinema-system/artifacts/dgtk80ji_2058E643-E510-4774-AEF5-0790ED1D0D88.png" },
  { key: "wall-history",  label: "Filmgeschichte 1895+",  url: "https://customer-assets-0z36b82j.emergentagent.net/job_home-cinema-system/artifacts/2b6nquc5_7B3B7650-BD95-49CF-94F2-D63DA7469124.png" },
];

const PHONE_PRESETS = [
  { key: "phone-popcorn", label: "Handy aus · Popcorn",   url: "https://customer-assets-0z36b82j.emergentagent.net/job_home-cinema-system/artifacts/2dstqexh_EA6A2E98-E643-4AF9-935F-0FDD8E9CA2E9.png" },
];

function RarUploadButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setProgress(`Lade ${files.length} Teil${files.length === 1 ? "" : "e"} hoch …`);
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      setProgress("Entpacke Archiv …");
      const { data } = await api.post("/uploads/rar-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      onDone?.(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, data.meta ? { ...data.meta, size_mb: data.size_mb } : null);
      toast.success(`Film entpackt · ${data.filename} · ${data.size_mb} MB`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Entpacken fehlgeschlagen");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };
  return (
    <label className={`inline-flex items-center gap-2 py-1.5 px-3 border border-dashed rounded cursor-pointer text-xs transitions-safe ${
      busy ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"
    }`} data-testid="rar-upload-btn">
      <Archive size={12} /> {busy ? progress : "🗜 RAR-Parts entpacken"}
      <input type="file" accept=".rar,.r00,.r01,.r02,.r03,.r04,.r05" multiple disabled={busy} className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))} />
    </label>
  );
}


function FoyerPreviewButton({ url }) {
  const [playing, setPlaying] = useState(false);
  const trimmed = (url || "").trim();
  const disabled = !trimmed;

  // Stop preview automatisch, wenn URL geleert oder gewechselt wird
  useEffect(() => { setPlaying(false); }, [trimmed]);

  return (
    <>
      <button type="button"
        onClick={() => !disabled && setPlaying((p) => !p)}
        disabled={disabled}
        data-testid="foyer-preview-btn"
        title={disabled ? "Erst URL oder Preset wählen" : (playing ? "Vorhören stoppen" : "Vorhören")}
        className={`shrink-0 min-w-[100px] px-3 py-2 rounded flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.28em] border transitions-safe ${
          disabled
            ? "border-[#1A1A1A] text-[#3A3A3A] cursor-not-allowed"
            : playing
              ? "border-[#7A1921] text-[#EE9CA1] bg-[#7A1921]/10 hover:bg-[#7A1921]/20"
              : "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20"
        }`}>
        {playing ? (<><Square size={12} /> Stop</>) : (<><Play size={12} /> Vorhören</>)}
      </button>
      {playing && !disabled && (
        <div data-testid="foyer-preview-active" className="hidden">
          {/* DisplayMusic rendert ein 1×1-Iframe für YouTube bzw. <audio> für .mp3 — sichtbarer Toggle unten rechts */}
          <DisplayMusic url={trimmed} />
        </div>
      )}
    </>
  );
}

export default function Showtimes() {
  const [rows, setRows] = useState([]);
  const [movies, setMovies] = useState([]);
  const [halls, setHalls] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [series, setSeries] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [prerollFor, setPrerollFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);

  const load = async () => {
    const [a, b, h, t, s] = await Promise.all([
      api.get("/showtimes"), api.get("/movies"), api.get("/seat-plans"),
      api.get("/admin/ticket-templates").catch(() => ({ data: [] })),
      api.get("/series").catch(() => ({ data: [] })),
    ]);
    setRows(a.data); setMovies(b.data); setHalls(h.data); setTemplates(t.data); setSeries(s.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Vorstellung löschen?")) return;
    await api.delete(`/showtimes/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="showtimes-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Programm</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Vorstellungen</h1>
        </div>
        <button data-testid="showtime-add-btn"
          onClick={async () => {
          const defaults = { movie_id: movies[0]?.id, date: new Date().toISOString().slice(0,10), entry_time: "19:15", trailer_time: "19:45", start_time: "20:00", hall: "Saal 1", ticket_price: 8, language: "Deutsch", subtitles: "", format: "2D", is_private: false, snackbar_preorder: true, welcome_text: "", event_type: "regular", status: "geplant", max_guests: 8 };
          // Standard-Ticketpreis aus /settings holen — Admin kann diesen dort setzen
          try { const { data: st } = await api.get("/settings"); if (st?.ticket_price) defaults.ticket_price = Number(st.ticket_price); } catch { /* ignore */ }
          setEdit(defaults); setOpen(true);
        }}
          className="flex items-center gap-2 bg-[#D4AF37] text-black px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe shrink-0">
          <Plus size={16} /> <span className="hidden xs:inline">Neue Vorstellung</span><span className="xs:hidden">Neu</span>
        </button>
      </div>

      {/* Desktop: Tabelle */}
      <div className="card-lux overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Datum</th>
              <th className="text-left p-3">Zeit</th>
              <th className="text-left p-3">Film</th>
              <th className="text-left p-3">Event</th>
              <th className="text-left p-3">Saal</th>
              <th className="text-left p-3">Preis</th>
              <th className="text-left p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ev = eventType(r.event_type);
              return (
              <tr key={r.id} className="border-b border-[#141414] hover:bg-[#0A0A0A]">
                <td className="p-3 font-mono whitespace-nowrap">{fmtDateEU(r.date)}</td>
                <td className="p-3 font-mono whitespace-nowrap">{r.start_time}</td>
                <td className="p-3">{r.movie?.title_de || "—"}</td>
                <td className="p-3 whitespace-nowrap"><span className="text-xs" style={{ color: ev.color }}>{ev.icon} {ev.l}</span></td>
                <td className="p-3 whitespace-nowrap">{r.hall}</td>
                <td className="p-3 whitespace-nowrap">{r.ticket_price?.toFixed(2)} €</td>
                <td className="p-3">
                  <span className="text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded-full px-2 py-0.5 whitespace-nowrap">
                    {STATUS.find((s) => s.v === r.status)?.l || r.status}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setPrerollFor(r)} data-testid={`preroll-btn-${r.id}`}
                    className={`text-xs px-2 py-1 rounded mr-2 inline-flex items-center gap-1 border transitions-safe ${
                      (r.preroll_trailers?.length || 0) > 0 ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"
                    }`} title="Trailer-Werbeblock">
                    <Youtube size={12} /> {(r.preroll_trailers?.length || 0) > 0 ? `${r.preroll_trailers.length} Preroll` : "Preroll"}
                  </button>
                  <a href={`/intro/${r.id}?op=1`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] mr-2 inline-flex items-center gap-1"><Play size={12} /> Intro</a>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/seat-qr-poster/${r.id}`} target="_blank" rel="noreferrer"
                    data-testid={`showtime-seat-qr-${r.id}`}
                    title="Snack-to-Seat QR-Poster (A4) drucken"
                    className="text-xs px-2 py-1 border border-[#EEDC82]/40 text-[#EEDC82] rounded hover:bg-[#8B7328]/20 mr-2 inline-flex items-center gap-1">
                    <QrCode size={12} /> Sitz-QRs
                  </a>
                  <DoorSignButton showtime={r} />
                  {r.status !== "abgesagt" && r.status !== "storniert" && (
                    <button onClick={() => setCancelFor(r)} data-testid={`showtime-cancel-${r.id}`}
                      title="Notfall-Absage"
                      className="text-xs px-2 py-1 border border-[#7A1921]/50 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20 mr-2 inline-flex items-center gap-1">
                      <AlertTriangle size={12} /> Absagen
                    </button>
                  )}
                  <button onClick={() => { setEdit(r); setOpen(true); }} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] mr-2"><Edit3 size={12} /></button>
                  <button onClick={() => remove(r.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
                </td>
              </tr>
            );})}
            {!rows.length && <tr><td colSpan={8} className="p-8 text-center text-[#A8A398]">Keine Vorstellungen</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {/* Mobile: Card-Layout */}
      <div className="md:hidden space-y-3" data-testid="showtimes-mobile-cards">
        {rows.map((r) => {
          const ev = eventType(r.event_type);
          const statusLabel = STATUS.find((s) => s.v === r.status)?.l || r.status;
          return (
            <div key={r.id} className="card-lux p-4 space-y-3" data-testid={`showtime-mobile-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg leading-tight">{r.movie?.title_de || "—"}</div>
                  <div className="text-xs text-[#A8A398] mt-1 font-mono">
                    {fmtDateEU(r.date)} · {r.start_time} · {r.hall}
                  </div>
                </div>
                <span className="text-[9px] uppercase tracking-widest border border-[#2A2A2A] rounded-full px-2 py-1 whitespace-nowrap shrink-0">
                  {statusLabel}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: ev.color }}>{ev.icon} {ev.l}</span>
                <span className="font-mono text-[#D4AF37]">{r.ticket_price?.toFixed(2)} €</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#141414]">
                <button onClick={() => setPrerollFor(r)}
                  className={`text-xs px-3 py-2 rounded inline-flex items-center gap-1.5 border ${
                    (r.preroll_trailers?.length || 0) > 0 ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398]"
                  }`}>
                  <Youtube size={13} /> {(r.preroll_trailers?.length || 0) > 0 ? `${r.preroll_trailers.length} Preroll` : "Preroll"}
                </button>
                <a href={`/intro/${r.id}?op=1`} target="_blank" rel="noreferrer" className="text-xs px-3 py-2 border border-[#2A2A2A] rounded inline-flex items-center gap-1.5"><Play size={13} /> Intro</a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/seat-qr-poster/${r.id}`} target="_blank" rel="noreferrer"
                  className="text-xs px-3 py-2 border border-[#EEDC82]/40 text-[#EEDC82] rounded inline-flex items-center gap-1.5">
                  <QrCode size={13} /> Sitz-QRs
                </a>
                <DoorSignButton showtime={r} />
                {r.status !== "abgesagt" && r.status !== "storniert" && (
                  <button onClick={() => setCancelFor(r)}
                    className="text-xs px-3 py-2 border border-[#7A1921]/50 text-[#EE9CA1] rounded inline-flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Absagen
                  </button>
                )}
                <button onClick={() => { setEdit(r); setOpen(true); }} className="text-xs px-3 py-2 border border-[#2A2A2A] rounded inline-flex items-center gap-1.5"><Edit3 size={13} /> Bearb.</button>
                <button onClick={() => remove(r.id)} className="text-xs px-3 py-2 border border-[#2A2A2A] rounded inline-flex items-center gap-1.5 text-[#EE9CA1]"><Trash2 size={13} /> Löschen</button>
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="card-lux p-8 text-center text-[#A8A398]">Keine Vorstellungen</div>}
      </div>

      {open && <ShowtimeDialog movies={movies} halls={halls} templates={templates} series={series} initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {prerollFor && <PrerollEditor showtime={prerollFor} onClose={() => setPrerollFor(null)} onSaved={() => { setPrerollFor(null); load(); }} />}
      {cancelFor && <NotfallDialog showtime={cancelFor} onClose={() => setCancelFor(null)} onDone={() => { setCancelFor(null); load(); }} />}
    </div>
  );
}

function DoorSignButton({ showtime }) {
  const [busy, setBusy] = useState(false);
  const body = { showtime_id: showtime.id };
  const preview = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signage/door-sign", body, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const printOff = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Türschild für „${showtime.movie?.title_de || "Vorstellung"}" (${showtime.hall}) an den Bürodrucker senden?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/signage/door-sign?print_office=true", body);
      toast.success("Türschild an Drucker gesendet");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bürodrucker nicht erreichbar"); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex rounded border border-[#EEDC82]/40 overflow-hidden mr-2">
      <button onClick={preview} disabled={busy}
        data-testid={`showtime-door-preview-${showtime.id}`}
        title="Türschild als PDF anzeigen"
        className="text-xs px-2 py-1 text-[#EEDC82] hover:bg-[#8B7328]/20 disabled:opacity-40 inline-flex items-center gap-1">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <DoorOpen size={12} />}
      </button>
      <button onClick={printOff} disabled={busy}
        data-testid={`showtime-door-print-${showtime.id}`}
        title="Türschild direkt an Bürodrucker"
        className="text-xs px-2 py-1 text-[#D4AF37] border-l border-[#EEDC82]/40 hover:bg-[#D4AF37]/10 disabled:opacity-40 inline-flex items-center gap-1">
        <Printer size={12} />
      </button>
    </span>
  );
}

function NotfallDialog({ showtime, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [refund, setRefund] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");

  const submit = async () => {
    if (!reason.trim()) return toast.error("Grund eingeben");
    if (confirm.trim().toUpperCase() !== "ABSAGEN") return toast.error("Zur Bestätigung „ABSAGEN\" eingeben");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/showtimes/${showtime.id}/cancel`, {
        reason: reason.trim(),
        notify_email: notifyEmail,
        refund_as_voucher: refund,
      });
      toast.success(`Abgesagt · ${data.affected_tickets} Tickets · ${data.vouchers_issued} Voucher erstellt`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#7A1921]/50 rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="notfall-dialog">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#1A1A1A] bg-[#7A1921]/10">
          <AlertTriangle size={20} className="text-[#EE9CA1]" />
          <div>
            <div className="font-heading text-xl text-[#EE9CA1]">Notfall-Modus</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Vorstellung absagen</div>
          </div>
          <button onClick={onClose} className="ml-auto text-[#A8A398] hover:text-[#F5F0E6]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-[#050505] border border-[#1A1A1A] rounded p-3 text-sm">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Vorstellung</div>
            <div className="mt-1 font-heading">{showtime.movie?.title_de || "?"}</div>
            <div className="text-xs text-[#A8A398] font-mono mt-1">{fmtDateEU(showtime.date)} · {showtime.start_time} · {showtime.hall}</div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Grund (wird an Gäste kommuniziert)</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="notfall-reason"
              placeholder="z.B. Technischer Defekt am Projektor · Krankheitsausfall · Stromausfall"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none text-sm" />
          </div>

          <div className="space-y-2 border-t border-[#1A1A1A] pt-4">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)}
                data-testid="notfall-notify-email" className="mt-0.5 accent-[#D4AF37]" />
              <span>
                <b>Gäste per E-Mail informieren</b>
                <div className="text-[10px] text-[#575148] mt-0.5">Automatische Absage-Mail an alle Ticketbesitzer mit Grund + Erstattungshinweis</div>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)}
                data-testid="notfall-refund" className="mt-0.5 accent-[#D4AF37]" />
              <span>
                <b>Gutschein-Erstattung ausstellen</b>
                <div className="text-[10px] text-[#575148] mt-0.5">Für jedes bezahlte Ticket wird ein Voucher zum Ticketwert erstellt · 12 Monate gültig</div>
              </span>
            </label>
          </div>

          <div className="border-t border-[#1A1A1A] pt-4">
            <label className="text-[10px] uppercase tracking-widest text-[#EE9CA1]">Zur Bestätigung &bdquo;ABSAGEN&ldquo; eingeben</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="notfall-confirm-input"
              placeholder="ABSAGEN"
              className="w-full mt-1 bg-[#050505] border border-[#7A1921]/40 rounded px-3 py-2 focus:border-[#EE9CA1] outline-none font-mono uppercase tracking-widest" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#1A1A1A]">
          <button onClick={onClose} className="py-2 px-4 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={submit} disabled={busy || confirm.trim().toUpperCase() !== "ABSAGEN"}
            data-testid="notfall-submit"
            className="py-2 px-6 bg-[#7A1921] hover:bg-[#8f2129] text-white rounded text-[11px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-2">
            <AlertTriangle size={13} /> {busy ? "…" : "Endgültig absagen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarathonMoviePicker({ movies, selectedIds, onChange }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  // TMDb-IDs → lokale Movies auflösen (nur Filme, die eine tmdb_id haben)
  const selected = React.useMemo(() => {
    const list = [];
    for (const tid of (selectedIds || [])) {
      const m = movies.find((x) => x.tmdb_id === tid);
      if (m) list.push(m);
      else list.push({ tmdb_id: tid, title_de: `TMDb #${tid}`, poster_url: null });
    }
    return list;
  }, [selectedIds, movies]);
  const available = React.useMemo(() => {
    const chosen = new Set(selectedIds || []);
    const query = q.trim().toLowerCase();
    return movies.filter((m) => m.tmdb_id && !chosen.has(m.tmdb_id))
      .filter((m) => !query || (m.title_de || "").toLowerCase().includes(query) || (m.title_original || "").toLowerCase().includes(query))
      .slice(0, 20);
  }, [movies, selectedIds, q]);
  const add = (m) => { onChange([...(selectedIds || []), m.tmdb_id]); setQ(""); };
  const remove = (tid) => onChange((selectedIds || []).filter((x) => x !== tid));
  const move = (idx, dir) => {
    const arr = [...(selectedIds || [])];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(arr);
  };
  return (
    <div className="mt-1 space-y-2" data-testid="marathon-picker">
      {selected.length > 0 && (
        <div className="space-y-1">
          {selected.map((m, i) => (
            <div key={m.tmdb_id} className="flex items-center gap-2 bg-[#050505] border border-[#1F1F1F] rounded p-2" data-testid={`marathon-chip-${m.tmdb_id}`}>
              <span className="text-[10px] font-mono text-[#575148] w-4">{i + 1}.</span>
              {m.poster_url ? (
                <img src={m.poster_url} alt="" className="w-6 h-9 object-cover rounded-sm" />
              ) : (
                <div className="w-6 h-9 bg-[#0F0F0F] rounded-sm flex items-center justify-center text-[8px] text-[#575148]">?</div>
              )}
              <div className="flex-1 truncate text-sm text-[#F5F0E6]">{m.title_de}</div>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-[10px] px-1.5 py-0.5 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] disabled:opacity-30">↑</button>
              <button type="button" onClick={() => move(i, +1)} disabled={i === selected.length - 1} className="text-[10px] px-1.5 py-0.5 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] disabled:opacity-30">↓</button>
              <button type="button" onClick={() => remove(m.tmdb_id)} data-testid={`marathon-remove-${m.tmdb_id}`}
                className="text-[10px] px-1.5 py-0.5 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#7A1921]">×</button>
            </div>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Film aus Bibliothek suchen & hinzufügen …"
          data-testid="marathon-search-input"
          className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] text-sm"
        />
        {open && available.length > 0 && (
          <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-[#0A0A0A] border border-[#2A2A2A] rounded shadow-2xl">
            {available.map((m) => (
              <button key={m.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(m)}
                data-testid={`marathon-add-${m.tmdb_id}`}
                className="w-full flex items-center gap-2 p-2 hover:bg-[#141414] border-b border-[#141414] last:border-0 text-left">
                {m.poster_url ? <img src={m.poster_url} alt="" className="w-6 h-9 object-cover rounded-sm" /> : <div className="w-6 h-9 bg-[#0F0F0F] rounded-sm" />}
                <span className="flex-1 truncate text-sm">{m.title_de}</span>
                {m.duration_min && <span className="text-[10px] font-mono text-[#575148]">{m.duration_min} min</span>}
              </button>
            ))}
          </div>
        )}
        {open && q.trim() && available.length === 0 && (
          <div className="absolute z-30 mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded p-3 text-xs text-[#575148]">
            Kein Film gefunden. Erst in <span className="text-[#D4AF37]">/admin/movies</span> anlegen.
          </div>
        )}
      </div>
    </div>
  );
}

function ShowtimeDialog({ movies, halls = [], templates = [], series = [], initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickHall = (id) => {
    const h = halls.find((x) => x.id === id);
    if (!h) return;
    setForm((f) => ({ ...f, seat_plan_id: h.id, hall: h.hall || h.name }));
  };

  const uploadWallpaper = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    upd("wallpaper_url")(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    toast.success("Hintergrund hochgeladen");
  };

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.patch(`/showtimes/${form.id}`, form);
      else await api.post(`/showtimes`, form);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">Vorstellung</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Operator-Fernsteuerung (Handy) — nur wenn Vorstellung schon gespeichert */}
          {form.id && (
            <div className="flex items-center gap-4 p-3 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04]" data-testid="showtime-remote-qr">
              <div className="bg-[#F5F0E6] p-1.5 rounded shrink-0">
                <QRCodeCanvas
                  value={typeof window !== "undefined" ? `${window.location.origin}/remote/${form.id}` : ""}
                  size={72}
                  bgColor="#F5F0E6"
                  fgColor="#0A0A0A"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">Handy-Fernsteuerung</div>
                <div className="text-sm text-[#F5F0E6] mt-1">Scanne mit deinem Handy während der Vorstellung: Skip · Blackout · Enthüllen · Neu starten</div>
                <div className="text-[10px] text-[#575148] mt-1 font-mono truncate">/remote/{form.id}</div>
              </div>
            </div>
          )}
          {/* TMDb Direktimport für neue Vorstellung */}
          {!initial.id && (
            <TmdbQuickImport
              onPicked={async (tmdb_id) => {
                try {
                  const { data } = await api.post(`/tmdb/import/${tmdb_id}`);
                  toast.success(`„${data.title_de}" importiert`);
                  // reload movie list und select
                  const list = await api.get("/movies");
                  window.dispatchEvent(new CustomEvent("movies-refreshed", { detail: list.data }));
                  setForm((f) => ({ ...f, movie_id: data.id, __movieTitle: data.title_de }));
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Film</label>
            <select data-testid="showtime-movie-select" value={form.movie_id || ""} onChange={(e) => upd("movie_id")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              <option value="">— wählen —</option>
              {movies.map((m) => <option key={m.id} value={m.id}>{m.title_de}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Saal</label>
            <select data-testid="showtime-hall-select" value={form.seat_plan_id || ""} onChange={(e) => pickHall(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              <option value="">{halls.length ? "— Saal wählen —" : "Bitte zuerst Saal anlegen"}</option>
              {halls.map((h) => <option key={h.id} value={h.id}>{h.name} ({h.capacity} Sitze)</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datum</label>
            <CinemaDatePicker value={form.date} onChange={(iso) => upd("date")(iso)} testid="showtime-date" />
          </div>
          <F label="Einlass (HH:MM)" v={form.entry_time} on={upd("entry_time")} />
          <F label="Trailer (HH:MM)" v={form.trailer_time} on={upd("trailer_time")} />
          <F label="Filmstart (HH:MM)" v={form.start_time} on={upd("start_time")} testid="showtime-start" />
          <F label="Ticketpreis (€)" v={form.ticket_price} on={(v) => upd("ticket_price")(parseFloat(v || 0))} type="number" />
          <F label="Max. Gäste" v={form.max_guests || ""} on={(v) => upd("max_guests")(parseInt(v || 0) || null)} type="number" />
          <F label="Sprache" v={form.language} on={upd("language")} />
          <F label="Untertitel" v={form.subtitles} on={upd("subtitles")} />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Format</label>
            <select value={form.format} onChange={(e) => upd("format")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              <option>2D</option><option>3D</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Status</label>
            <select value={form.status} onChange={(e) => upd("status")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Event-Typ</label>
            <select value={form.event_type || "regular"} onChange={(e) => upd("event_type")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              {EVENT_TYPES.map((e) => <option key={e.v} value={e.v}>{e.icon} {e.l}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Ticket-Vorlage</label>
            <select value={form.ticket_template_id || ""} onChange={(e) => upd("ticket_template_id")(e.target.value || null)}
              data-testid="showtime-ticket-template"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              <option value="">Standard-Vorlage verwenden</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " · Standard" : ""}</option>)}
            </select>
            <div className="text-[10px] text-[#575148] mt-1">
              Legt fest, welches Bon-Layout beim Ausdruck der Tickets dieser Vorstellung verwendet wird (z.B. VIP Night · Family Matinee · Gala Evening).
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Filmreihe</label>
            <div className="flex gap-2 mt-1">
              <select value={form.series_id || ""} onChange={(e) => upd("series_id")(e.target.value || null)}
                data-testid="showtime-series"
                className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                <option value="">— Keine Filmreihe —</option>
                {series.map((s) => <option key={s.id} value={s.id}>{s.name}{s.show_on_landing ? " · öffentlich" : ""}{s.marathon_price ? ` · Marathon ${s.marathon_price}€` : ""}</option>)}
              </select>
              {form.series_id && (() => {
                const sel = series.find((x) => x.id === form.series_id);
                if (!sel) return null;
                const applyFromSeries = () => {
                  const patch = { marathon_tmdb_ids: [] };
                  // Movies aus Reihe zu marathon_tmdb_ids mappen
                  const seriesMovies = (sel.movie_ids || []).map((mid) => movies.find((m) => m.id === mid)).filter(Boolean);
                  patch.marathon_tmdb_ids = seriesMovies.map((m) => m.tmdb_id).filter(Boolean);
                  // Musik + Wandbild kopieren, wenn Showtime-Felder leer sind
                  if (!form.foyer_music_url && sel.foyer_music_url) patch.foyer_music_url = sel.foyer_music_url;
                  if (!form.wall_music_url && sel.wall_music_url) patch.wall_music_url = sel.wall_music_url;
                  if (!form.pre_foyer_wall_url && sel.pre_foyer_wall_url) patch.pre_foyer_wall_url = sel.pre_foyer_wall_url;
                  if (!form.pre_foyer_wall_duration_min && sel.pre_foyer_wall_duration_min) patch.pre_foyer_wall_duration_min = sel.pre_foyer_wall_duration_min;
                  setForm((f) => ({ ...f, ...patch }));
                  toast.success(`${seriesMovies.length} Filme + Musik/Wand übernommen`);
                };
                return (
                  <button type="button" onClick={applyFromSeries} data-testid="showtime-series-apply"
                    className="inline-flex items-center gap-1 px-3 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 whitespace-nowrap">
                    ⤵ Filme &amp; Musik übernehmen
                  </button>
                );
              })()}
            </div>
            <div className="text-[10px] text-[#575148] mt-1">
              Gruppiert diese Vorstellung mit einer Filmreihe. Der Button setzt Marathon-Filme, Musik + Wandbild aus der Reihe.
            </div>
          </div>
          <div className="md:col-span-2 flex items-center gap-6">
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.is_private} onChange={(e) => upd("is_private")(e.target.checked)} /> Private Vorstellung
            </label>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.snackbar_preorder} onChange={(e) => upd("snackbar_preorder")(e.target.checked)} /> Snackvorbestellung
            </label>
          </div>
          {form.is_private && <F label="Einladungscode" v={form.invite_code || ""} on={upd("invite_code")} />}
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Begrüßungstext</label>
            <textarea rows={2} value={form.welcome_text || ""} onChange={(e) => upd("welcome_text")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="md:col-span-2 border-t border-[#1A1A1A] pt-4">
            <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Foyer-Phase · Ruhezeit mit Logo & Musik vor dem Vorspann</label>
            <div className="mt-2 grid md:grid-cols-[1fr_140px] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Foyer-Musik (optional · mehrere Tracks für längere Playlist)</label>
                <div className="mt-1 flex gap-2">
                  <input value={form.foyer_music_url || ""} onChange={(e) => upd("foyer_music_url")(e.target.value)}
                    data-testid="showtime-foyer-music"
                    placeholder="Spotify · YouTube · Audio-URL · mehrere mit | trennen"
                    className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono text-xs" />
                  <FoyerPreviewButton url={(form.foyer_music_url || "").split("|")[0]?.trim()} />
                  <label className="inline-flex items-center gap-1.5 py-1.5 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe whitespace-nowrap" data-testid="foyer-audio-upload">
                    🎵 Hochladen
                    <input type="file" multiple accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/aac,audio/ogg,audio/*" className="hidden" onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      const existing = (form.foyer_music_url || "").split("|").map((s) => s.trim()).filter(Boolean);
                      const newUrls = [];
                      for (const f of files) {
                        const fd = new FormData(); fd.append("file", f);
                        try {
                          const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          newUrls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
                        } catch (err) { toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"}`); }
                      }
                      if (newUrls.length) {
                        upd("foyer_music_url")([...existing, ...newUrls].join("|"));
                        toast.success(`${newUrls.length} Track${newUrls.length === 1 ? "" : "s"} hinzugefügt`);
                      }
                      e.target.value = "";
                    }} />
                  </label>
                </div>
                {/* Track-Liste bei mehreren URLs */}
                {(() => {
                  const tracks = (form.foyer_music_url || "").split("|").map((s) => s.trim()).filter(Boolean);
                  if (tracks.length <= 1) return null;
                  return (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="foyer-track-list">
                      {tracks.map((t, i) => (
                        <div key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.04] text-[10px] font-mono">
                          <span className="text-[#D4AF37]">Track {i + 1}</span>
                          <span className="text-[#A8A398] truncate max-w-[180px]">{t.split("/").pop()?.split("?")[0] || t}</span>
                          <button type="button" data-testid={`foyer-track-remove-${i}`}
                            onClick={() => upd("foyer_music_url")(tracks.filter((_, j) => j !== i).join("|"))}
                            className="text-[#575148] hover:text-[#EE9CA1]">×</button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="mt-2 flex flex-wrap gap-2">
                  {FOYER_PRESETS.map((p) => {
                    const active = (form.foyer_music_url || "").trim() === p.url;
                    return (
                      <button key={p.key} type="button" data-testid={`foyer-preset-${p.key}`}
                        onClick={() => upd("foyer_music_url")(p.url)}
                        className={`text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border transitions-safe ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"}`}>
                        {p.icon} {p.label}
                      </button>
                    );
                  })}
                  {(form.foyer_music_url || "").trim() && (
                    <button type="button" data-testid="foyer-preset-clear"
                      onClick={() => upd("foyer_music_url")("")}
                      className="text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border border-[#2A2A2A] text-[#575148] hover:border-[#7A1921] hover:text-[#EE9CA1]">
                      × Leeren
                    </button>
                  )}
                </div>
                <SpotifyPlaylistPicker
                  currentUrl={form.foyer_music_url || ""}
                  onPick={(u) => {
                    const existing = (form.foyer_music_url || "").split("|").map((s) => s.trim()).filter(Boolean);
                    if (existing.includes(u)) return;
                    upd("foyer_music_url")([...existing, u].join("|"));
                  }}
                  onReplace={(u) => upd("foyer_music_url")(u)}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Dauer (Min)</label>
                <input type="number" min={1} max={20}
                  value={form.foyer_duration_min ?? 6}
                  onChange={(e) => upd("foyer_duration_min")(parseInt(e.target.value, 10) || 6)}
                  data-testid="showtime-foyer-duration"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
              </div>
            </div>
            <div className="text-[10px] text-[#A8A398] mt-1">Empfohlen 5–7 Minuten. Zeigt Movie-House-Logo + Countdown, Musik im Hintergrund. Danach kommen Text · Werbung · Vorspann · Film.</div>

            {/* Vorhang beim Filmstart — Öffnungs-Dauer */}
            <div className="mt-4 grid md:grid-cols-[1fr_160px] gap-3 items-end">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Vorhang-Öffnung beim Filmstart</label>
                <div className="text-[10px] text-[#A8A398] mt-1">Roter Samt-Vorhang gleitet zur Seite, bevor der Film startet. Standard 2,8 s — höher = feierlicher.</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Dauer (Sek.)</label>
                <input type="number" step={0.1} min={0} max={12}
                  value={form.curtain_open_seconds ?? 2.8}
                  onChange={(e) => upd("curtain_open_seconds")(Math.max(0, Math.min(12, parseFloat(e.target.value) || 0)))}
                  data-testid="showtime-curtain-seconds"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono" />
              </div>
            </div>

            {/* Wandbild VOR dem Foyer */}
            <div className="mt-6 border-t border-[#1A1A1A] pt-4">
              <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Wandbild vor dem Foyer · Kino-Galerie</label>
              <div className="text-[10px] text-[#A8A398] mt-1">Wird VOR dem Foyer 0-10 Min. angezeigt (z.B. wenn Gäste eintreffen). 0 Min = deaktiviert. Pro Vorstellung frei wählbar.</div>

              <div className="mt-3 grid md:grid-cols-[1fr_140px] gap-3 items-start">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bild-URL</label>
                  <div className="mt-1 flex gap-2">
                    <input value={form.pre_foyer_wall_url || ""} onChange={(e) => {
                        const v = e.target.value;
                        upd("pre_foyer_wall_url")(v);
                        // Auto-Dauer: sobald eine URL gesetzt wird und noch 0 → 3 Min
                        if (v && !Number(form.pre_foyer_wall_duration_min)) upd("pre_foyer_wall_duration_min")(3);
                      }}
                      data-testid="showtime-wall-url"
                      placeholder="Preset wählen oder eigene URL / Upload"
                      className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono text-xs" />
                    <label className="inline-flex items-center gap-1.5 py-1.5 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe whitespace-nowrap" data-testid="wall-image-upload">
                      🖼️ Hochladen
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData(); fd.append("file", f);
                        try {
                          const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          upd("pre_foyer_wall_url")(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
                          if (!Number(form.pre_foyer_wall_duration_min)) upd("pre_foyer_wall_duration_min")(3);
                          toast.success("Wandbild hochgeladen · Dauer auf 3 Min gesetzt");
                        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"); }
                        e.target.value = "";
                      }} />
                    </label>
                  </div>

                  {/* Preset-Grid */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {WALL_PRESETS.map((p) => {
                      const active = (form.pre_foyer_wall_url || "").trim() === p.url;
                      return (
                        <button key={p.key} type="button" data-testid={`wall-preset-${p.key}`}
                          onClick={() => {
                            upd("pre_foyer_wall_url")(p.url);
                            if (!Number(form.pre_foyer_wall_duration_min)) upd("pre_foyer_wall_duration_min")(3);
                          }}
                          className={`group relative aspect-video rounded border overflow-hidden transitions-safe ${active ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/40" : "border-[#2A2A2A] hover:border-[#D4AF37]/60"}`}>
                          <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                          <div className={`absolute inset-x-0 bottom-0 px-2 py-1 text-[9px] uppercase tracking-widest ${active ? "bg-[#D4AF37] text-black" : "bg-black/70 text-[#F5F0E6]"}`}>
                            {p.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {(form.pre_foyer_wall_url || "").trim() && (
                    <button type="button" data-testid="wall-preset-clear"
                      onClick={() => { upd("pre_foyer_wall_url")(""); upd("pre_foyer_wall_duration_min")(0); }}
                      className="mt-2 text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border border-[#2A2A2A] text-[#575148] hover:border-[#7A1921] hover:text-[#EE9CA1]">
                      × Wandbild entfernen
                    </button>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Dauer (Min.)</label>
                  <input type="number" min={0} max={10}
                    value={form.pre_foyer_wall_duration_min ?? 0}
                    onChange={(e) => upd("pre_foyer_wall_duration_min")(Math.max(0, Math.min(10, parseInt(e.target.value, 10) || 0)))}
                    data-testid="showtime-wall-duration"
                    className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono" />
                  <div className="text-[9px] text-[#575148] mt-1">0-10 Min · 0 = aus</div>
                </div>
              </div>
            </div>

            {/* Handy-aus-Hinweis · Bild-Override */}
            <div className="mt-6 border-t border-[#1A1A1A] pt-4">
              <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Handy-aus-Hinweis · Bild statt Text</label>
              <div className="text-[10px] text-[#A8A398] mt-1">Ohne Bild wird der Standard-Textslide angezeigt. Mit Bild wird der komplette Fullscreen-Slide gezeigt.</div>

              <div className="mt-3 flex gap-2">
                <input value={form.phone_image_url || ""} onChange={(e) => upd("phone_image_url")(e.target.value)}
                  data-testid="showtime-phone-url"
                  placeholder="Preset wählen oder eigene URL / Upload"
                  className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] font-mono text-xs" />
                <label className="inline-flex items-center gap-1.5 py-1.5 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe whitespace-nowrap" data-testid="phone-image-upload">
                  🖼️ Hochladen
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      upd("phone_image_url")(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
                      toast.success("Handy-Bild hochgeladen");
                    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"); }
                    e.target.value = "";
                  }} />
                </label>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {PHONE_PRESETS.map((p) => {
                  const active = (form.phone_image_url || "").trim() === p.url;
                  return (
                    <button key={p.key} type="button" data-testid={`phone-preset-${p.key}`}
                      onClick={() => upd("phone_image_url")(p.url)}
                      className={`group relative aspect-video rounded border overflow-hidden transitions-safe ${active ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/40" : "border-[#2A2A2A] hover:border-[#D4AF37]/60"}`}>
                      <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                      <div className={`absolute inset-x-0 bottom-0 px-2 py-1 text-[9px] uppercase tracking-widest ${active ? "bg-[#D4AF37] text-black" : "bg-black/70 text-[#F5F0E6]"}`}>
                        {p.label}
                      </div>
                    </button>
                  );
                })}
              </div>
              {(form.phone_image_url || "").trim() && (
                <button type="button" data-testid="phone-preset-clear"
                  onClick={() => upd("phone_image_url")("")}
                  className="mt-2 text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border border-[#2A2A2A] text-[#575148] hover:border-[#7A1921] hover:text-[#EE9CA1]">
                  × Zurück zum Text-Standard
                </button>
              )}
            </div>

            {/* Anzahl Trailer im Vorspann */}
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Trailer im Vorspann (0–10)</label>
                <input type="number" min={0} max={10}
                  value={form.preroll_trailer_count ?? 5}
                  onChange={(e) => upd("preroll_trailer_count")(Math.max(0, Math.min(10, parseInt(e.target.value, 10) || 0)))}
                  data-testid="showtime-preroll-count"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
                <div className="text-[10px] text-[#575148] mt-1">0 = keine Trailer, direkt zum Film</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">🎬 Filme-Marathon</label>
                <MarathonMoviePicker
                  movies={movies}
                  selectedIds={form.marathon_tmdb_ids || []}
                  onChange={upd("marathon_tmdb_ids")}
                />
                <div className="text-[10px] text-[#575148] mt-1">Wählen: Nach dem Hauptfilm werden diese Filme nacheinander mit Übergangs-Karte gestartet</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Custom Wallpaper (Foyer-TV & Begrüßungs-Display)</label>
            <div className="mt-2 grid md:grid-cols-[1fr_auto_auto] gap-2">
              <input value={form.wallpaper_url || ""} onChange={(e) => upd("wallpaper_url")(e.target.value)}
                placeholder="URL oder hochladen" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
              <button type="button" onClick={() => setPickerOpen(true)} data-testid="showtime-wallpaper-library"
                className="inline-flex items-center gap-2 py-2 px-3 border border-[#2A2A2A] rounded text-xs text-[#D4AF37] hover:border-[#D4AF37] transitions-safe whitespace-nowrap">
                📚 Bibliothek
              </button>
              <label className="inline-flex items-center gap-2 py-2 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe whitespace-nowrap">
                Bild hochladen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadWallpaper(e.target.files[0])} data-testid="showtime-wallpaper-upload" />
              </label>
            </div>
            {form.wallpaper_url && <img src={form.wallpaper_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="mt-2 w-full max-h-40 object-cover rounded" />}
            <div className="text-[10px] text-[#A8A398] mt-1">Überschreibt das Filmbild auf öffentlichen Bildschirmen während der Ansage.</div>
          </div>
          <div className="md:col-span-2 border-t border-[#1A1A1A] pt-4">
            <SeatBlocksEditor
              blocks={form.blocked_seats || []}
              onChange={(next) => upd("blocked_seats")(next)}
            />
          </div>
          <div className="md:col-span-2 border-t border-[#1A1A1A] pt-4">
            <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold">?</span>
              Setup-Assistent · Wie startest du den Film?
            </label>
            <div className="text-[10px] text-[#A8A398] mt-1 mb-3">Wähle die Quelle — nur passende Felder werden eingeblendet.</div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { v: "appletv", l: "🍿 Apple TV / HDMI", d: "Vorhang schwarz nach Vorspann. Du startest per Fernbedienung oder HDMI-Switch." },
                { v: "video", l: "🎞 Videodatei (MP4)", d: "Autoplay einer eigenen Videodatei. Empfohlen bei einzelnem Bildschirm." },
                { v: "stream", l: "▶ Streaming-Link", d: "YouTube · Vimeo · Plex · Jellyfin — läuft im Browser weiter." },
              ].map((o) => (
                <button key={o.v} type="button" data-testid={`film-source-${o.v}`}
                  onClick={() => upd("film_source_kind")(o.v)}
                  className={`text-left p-3 rounded border transitions-safe ${
                    (form.film_source_kind || "appletv") === o.v ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]/50"
                  }`}>
                  <div className="text-sm">{o.l}</div>
                  <div className="text-[10px] text-[#A8A398] mt-1">{o.d}</div>
                </button>
              ))}
            </div>

            {/* Kontext-Erklärung nach Auswahl */}
            {(form.film_source_kind || "appletv") === "appletv" && (
              <div className="mt-3 p-3 rounded border border-[#D4AF37]/25 bg-[#D4AF37]/5">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Nach Vorspann</div>
                <div className="text-xs text-[#F5F0E6]/85 mt-1 leading-relaxed">
                  Bildschirm zeigt dezenten <b>Logo-Vorhang</b> auf Schwarz — <b>unendlich</b>, kein Timer. Publikum sieht nichts vom Umschalten.
                  Du steuerst per Tastatur (Leertaste/Enter/F/Esc) oder per <b>Handy-QR</b>, der oben links im Foyer eingeblendet wird.
                </div>
              </div>
            )}
            {(form.film_source_kind === "video" || form.film_source_kind === "stream") && (
              <div className="mt-3 space-y-2">
                <div className="p-3 rounded border border-[#D4AF37]/25 bg-[#D4AF37]/5 text-xs text-[#F5F0E6]/85 leading-relaxed">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] block mb-1">Nach Vorspann</span>
                  {form.film_source_kind === "video"
                    ? "Deine Videodatei wird automatisch im Vollbild abgespielt — kein Umschalten nötig."
                    : "Der Stream läuft direkt im Browser weiter — kein Cmd+Tab, kein Apple TV nötig."}
                </div>
                <input value={form.film_source_url || ""} onChange={(e) => upd("film_source_url")(e.target.value)}
                  data-testid="film-source-url"
                  placeholder={form.film_source_kind === "video" ? "https://.../film.mp4  oder  MP4 hochladen" : "https://youtube.com/watch?v=…  oder  https://vimeo.com/…"}
                  className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
                {form.film_source_kind === "video" && (
                  <VideoUploadWithProgress
                    onDone={(url, meta) => { upd("film_source_url")(url); if (meta) upd("film_source_meta")(meta); }}
                  />
                )}
                {form.film_source_kind === "video" && form.film_source_meta?.codec && (
                  <VideoMetaChips meta={form.film_source_meta} />
                )}
              </div>
            )}
          </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="showtime-save-btn" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe">
            {busy ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
      {pickerOpen && <WallpaperPicker onPick={(u) => upd("wallpaper_url")(u)} onClose={() => setPickerOpen(false)} />}
    </div>
  );
}

function SpotifyPlaylistPicker({ currentUrl, onPick, onReplace }) {
  const [open, setOpen] = useState(false);
  const [activeMood, setActiveMood] = useState(0);
  // Prüfe alle bereits eingetragenen Tracks auf Spotify
  const tracks = (currentUrl || "").split("|").map((s) => s.trim()).filter(Boolean);
  const spotifyTracks = tracks.filter(isSpotifyUrl);

  return (
    <div className="mt-3 border-t border-[#1A1A1A] pt-3" data-testid="spotify-picker">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-[#1DB954] flex items-center gap-1">
            🎵 Spotify-Playlist einfügen
          </span>
          {spotifyTracks.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#1DB954]/15 text-[#1DB954] border border-[#1DB954]/30" data-testid="spotify-detected">
              {spotifyTracks.length} Spotify-Link{spotifyTracks.length > 1 ? "s" : ""} aktiv
            </span>
          )}
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="spotify-picker-toggle"
          className="text-[10px] uppercase tracking-widest px-3 py-1 rounded border border-[#1DB954]/40 text-[#1DB954] hover:bg-[#1DB954]/10">
          {open ? "Zuklappen ×" : "Vorschläge zeigen ↓"}
        </button>
      </div>
      <div className="text-[10px] text-[#575148] mt-1 italic">
        Kopiere z. B. <span className="font-mono">https://open.spotify.com/playlist/...</span> in das Eingabefeld — auch mit <span className="font-mono">?si=</span>-Tracking-Params.
      </div>
      {open && (
        <div className="mt-3 space-y-3" data-testid="spotify-picker-panel">
          {/* Mood-Tabs */}
          <div className="flex flex-wrap gap-1.5">
            {SPOTIFY_MOODS.map((m, i) => (
              <button key={m.name} type="button" onClick={() => setActiveMood(i)}
                data-testid={`spotify-mood-${i}`}
                className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transitions-safe ${
                  activeMood === i ? "border-[#1DB954] bg-[#1DB954]/10 text-[#1DB954]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#1DB954]/60"
                }`}>
                {m.emoji} {m.name}
              </button>
            ))}
          </div>
          {/* Playlist-Karten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(SPOTIFY_MOODS[activeMood]?.playlists || []).map((p) => {
              const inList = tracks.includes(p.url);
              return (
                <div key={p.key} className={`flex items-center gap-2 p-2 rounded border ${inList ? "border-[#1DB954] bg-[#1DB954]/[0.06]" : "border-[#2A2A2A] hover:border-[#1DB954]/40"}`}
                  data-testid={`spotify-preset-${p.key}`}>
                  <div className="text-[#1DB954] text-lg">🎵</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-[#F5F0E6] truncate">{p.label}</div>
                    <div className="text-[9px] font-mono text-[#575148] truncate">{p.url.replace("https://open.spotify.com/", "…/")}</div>
                  </div>
                  {inList ? (
                    <span className="text-[9px] uppercase tracking-widest text-[#1DB954]">Aktiv</span>
                  ) : (
                    <>
                      <button type="button" onClick={() => onPick(p.url)} data-testid={`spotify-add-${p.key}`}
                        className="text-[9px] uppercase tracking-widest px-2 py-1 rounded border border-[#1DB954]/40 text-[#1DB954] hover:bg-[#1DB954]/10">
                        + Hinzufügen
                      </button>
                      <button type="button" onClick={() => onReplace(p.url)} data-testid={`spotify-replace-${p.key}`}
                        className="text-[9px] uppercase tracking-widest px-2 py-1 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                        Ersetzen
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Live-Spotify-Embed-Vorschau — zeigt den ersten Spotify-Track */}
      {spotifyTracks.length > 0 && (() => {
        const first = spotifyTracks[0];
        const m = first.match(/open\.spotify\.com\/(?:intl-\w+\/)?(playlist|album|track|artist|episode|show)\/([\w]+)/i);
        if (!m) return null;
        const src = `https://open.spotify.com/embed/${m[1].toLowerCase()}/${m[2]}?utm_source=generator`;
        return (
          <div className="mt-3" data-testid="spotify-preview">
            <div className="text-[10px] uppercase tracking-widest text-[#1DB954] mb-1">Vorschau · Erster Spotify-Track</div>
            <iframe src={src} title="spotify-preview" width="100%" height="80"
              className="rounded border border-[#1DB954]/30"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture" loading="lazy" />
          </div>
        );
      })()}
    </div>
  );
}

function VideoUploadWithProgress({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");   // uploading | probing | remuxing | transcoding | storing | done | error
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [etaSec, setEtaSec] = useState(null);

  const PHASE_LABEL = {
    waiting: "Bereit", uploading: "Upload läuft …", probing: "Codec-Analyse",
    remuxing: "Container-Remux (verlustfrei)", transcoding: "H.264-Konvertierung",
    storing: "Speichere ins Object-Storage", done: "Fertig", error: "Fehler",
  };
  const isHevcRemux = phase === "remuxing" && /hevc|h265/i.test(message);

  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setPhase("uploading"); setPercent(0); setMessage(`Sende ${(f.size / (1024 * 1024)).toFixed(1)} MB …`); setEtaSec(null);
    try {
      // 1) Job initialisieren
      const { data: init } = await api.post("/uploads/job/init");
      const jobId = init.job_id;
      // 2) Polling starten für Backend-Phasen (probing/remuxing/transcoding/storing)
      let polling = true;
      const poll = async () => {
        while (polling) {
          try {
            const { data: p } = await api.get(`/uploads/progress/${jobId}`);
            if (p.phase && p.phase !== "waiting") {
              setPhase(p.phase);
              setPercent(p.percent);
              setMessage(p.message || "");
              setEtaSec(p.eta_sec);
            }
            if (p.done) { polling = false; break; }
          } catch { /* ignore polling errors */ }
          await new Promise((r) => setTimeout(r, 800));
        }
      };
      poll();
      // 3) Upload mit XHR-Progress (0-40%)
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data", "X-Upload-Job": jobId },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const p = (evt.loaded / evt.total) * 40;
            setPercent(p);
            setMessage(`${(evt.loaded / (1024 * 1024)).toFixed(1)} / ${(evt.total / (1024 * 1024)).toFixed(1)} MB`);
          }
        },
      });
      polling = false;
      setPhase("done"); setPercent(100); setMessage("Fertig");
      onDone(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, data.meta ? { ...data.meta, size_mb: data.size_mb } : null);
      toast.success("Video hochgeladen");
    } catch (err) {
      setPhase("error");
      setMessage(err.response?.data?.detail || "Upload fehlgeschlagen");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="video-upload-progress">
      {!busy && phase !== "done" && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 py-1.5 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe">
            🎞 Videodatei hochladen
            <input type="file" accept="video/mp4,video/webm,video/x-matroska,video/quicktime,video/mp2t,.mp4,.mkv,.mov,.avi,.webm,.m4v,.ts,.m2ts,.hevc,.h265,video/*" className="hidden" data-testid="video-upload-input" onChange={handleFile} />
          </label>
          <RarUploadButton onDone={onDone} />
          <span className="text-[10px] text-[#575148] uppercase tracking-widest">MP4 · MKV · MOV · HEVC/H.265 · AVI · RAR</span>
        </div>
      )}
      {(busy || phase === "done" || phase === "error") && (
        <div className={`p-3 rounded border ${
          phase === "error" ? "border-[#7A1921]/50 bg-[#7A1921]/10" :
          phase === "done"  ? "border-[#8FBC8F]/40 bg-[#0F1F0F]/40" :
          "border-[#D4AF37]/40 bg-[#0F0D08]"
        }`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]" data-testid="upload-phase-label">
                {PHASE_LABEL[phase] || phase}
              </span>
              {isHevcRemux && (
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#EEDC82] rounded">HEVC-Copy</span>
              )}
            </div>
            <span className="text-xs font-mono text-[#F5F0E6]" data-testid="upload-percent">
              {percent.toFixed(1)}%
              {etaSec != null && etaSec > 0 && (
                <span className="ml-2 text-[#575148]">· ETA {etaSec >= 60 ? `${Math.floor(etaSec / 60)}m${etaSec % 60}s` : `${etaSec}s`}</span>
              )}
            </span>
          </div>
          <div className="h-2 bg-[#050505] rounded overflow-hidden">
            <div className={`h-full transitions-safe ${
              phase === "error" ? "bg-[#EE9CA1]" :
              phase === "done"  ? "bg-[#8FBC8F]" :
              phase === "transcoding" ? "bg-[#EEDC82]" :
              "bg-[#D4AF37]"
            }`} style={{ width: `${Math.max(2, Math.min(100, percent))}%` }} />
          </div>
          <div className="text-[10px] text-[#A8A398] mt-2 truncate" data-testid="upload-message">{message}</div>
          {phase === "done" && (
            <button onClick={() => { setPhase(""); setPercent(0); setMessage(""); }} className="mt-2 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#EEDC82]">
              Weiteres Video hochladen
            </button>
          )}
          {phase === "error" && (
            <button onClick={() => { setPhase(""); setPercent(0); setMessage(""); }} className="mt-2 text-[10px] uppercase tracking-widest text-[#EE9CA1] hover:text-white">
              Erneut versuchen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function VideoMetaChips({ meta }) {
  if (!meta || !meta.codec) return null;
  const { codec, width, height, duration_sec, size_mb } = meta;
  const isHevc = /hevc|h265/i.test(codec);
  const isH264 = /h264|avc/i.test(codec);
  const resLabel = width && height ? `${width}×${height}` : "";
  const durMin = duration_sec ? Math.round(duration_sec / 60) : 0;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid="film-source-meta">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border ${
        isHevc ? "border-[#D4AF37] text-[#EEDC82] bg-[#D4AF37]/10" :
        isH264 ? "border-[#8FBC8F]/50 text-[#B7E1B7] bg-[#8FBC8F]/10" :
        "border-[#2A2A2A] text-[#A8A398]"
      }`}>
        {isHevc ? "HEVC · H.265" : isH264 ? "H.264 · AVC" : String(codec).toUpperCase()}
      </span>
      {resLabel && (
        <span className="px-2 py-0.5 rounded text-[10px] font-mono border border-[#2A2A2A] text-[#A8A398]">
          {resLabel}{height >= 2160 ? " · 4K" : height >= 1080 ? " · Full HD" : height >= 720 ? " · HD" : ""}
        </span>
      )}
      {durMin > 0 && (
        <span className="px-2 py-0.5 rounded text-[10px] font-mono border border-[#2A2A2A] text-[#A8A398]">
          {Math.floor(durMin / 60) > 0 ? `${Math.floor(durMin / 60)}h ${durMin % 60}m` : `${durMin} min`}
        </span>
      )}
      {size_mb > 0 && (
        <span className="px-2 py-0.5 rounded text-[10px] font-mono border border-[#2A2A2A] text-[#575148]">
          {size_mb >= 1024 ? `${(size_mb / 1024).toFixed(1)} GB` : `${Math.round(size_mb)} MB`}
        </span>
      )}
      {isHevc && (
        <span className="text-[10px] text-[#575148] italic">
          verlustfrei remuxed · Safari/Chrome/Edge nativ · Firefox: Konvertierung nötig
        </span>
      )}
    </div>
  );
}

function F({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
    </div>
  );
}

function TmdbQuickImport({ onPicked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/tmdb/search`, { params: { q } });
      setResults(data.slice(0, 8));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pick = async (tmdb_id) => {
    setBusy(true);
    try {
      await onPicked(tmdb_id);
      setResults([]); setQ(""); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="card-lux p-3" data-testid="showtime-tmdb-quick">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2">
          <Search size={12} /> Film aus TMDb suchen &amp; importieren
        </div>
        <span className="text-[#A8A398] text-xs">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              data-testid="showtime-tmdb-input"
              placeholder="Titel eingeben — Beschreibung, Poster, Trailer werden automatisch geladen"
              className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none text-sm" />
            <button type="button" data-testid="showtime-tmdb-search" onClick={search} disabled={busy}
              className="bg-[#D4AF37] text-black px-4 py-2 rounded text-xs uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-50">
              Suchen
            </button>
          </div>
          {results.length > 0 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {results.map((r) => (
                <button key={r.tmdb_id} type="button" onClick={() => pick(r.tmdb_id)}
                  data-testid={`showtime-tmdb-pick-${r.tmdb_id}`}
                  disabled={busy}
                  className="text-left bg-[#050505] border border-[#2A2A2A] rounded overflow-hidden hover:border-[#D4AF37] transitions-safe disabled:opacity-40">
                  <div className="aspect-[2/3] bg-[#1A1A1A]">
                    {r.poster_url && <img src={r.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-1.5">
                    <div className="text-[11px] truncate leading-tight">{r.title}</div>
                    <div className="text-[9px] text-[#A8A398]">{r.year}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="text-[10px] text-[#575148] mt-2">Ein Klick importiert Film inkl. Poster, Backdrop, Beschreibung, Regie, Cast, Trailer &amp; FSK.</div>
        </div>
      )}
    </div>
  );
}


function SeatBlocksEditor({ blocks, onChange }) {
  const [seat, setSeat] = React.useState("");
  const [reason, setReason] = React.useState("defekt");
  const REASONS = [
    { v: "defekt",  l: "Defekt",             color: "#7A1921" },
    { v: "privat",  l: "Privat reserviert",  color: "#D4AF37" },
    { v: "wartung", l: "Wartung",            color: "#8B7328" },
    { v: "sonstiges", l: "Sonstiges",        color: "#575148" },
  ];

  const add = () => {
    const s = (seat || "").trim().toUpperCase();
    if (!s) return;
    if ((blocks || []).some((b) => (b.seat || "").toUpperCase() === s)) return;
    onChange([...(blocks || []), { seat: s, reason }]);
    setSeat("");
  };

  const remove = (s) => {
    onChange((blocks || []).filter((b) => b.seat !== s));
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-2">
        🚫 Sitzplatz-Sperren · nur für diese Vorstellung
      </label>
      <div className="text-[10px] text-[#A8A398] mt-1">Blockiert Sitze exklusiv für diesen Tag &amp; diesen Film — z. B. defekte Sitze oder für Privatgäste reservierte Plätze. Gilt NICHT für andere Vorstellungen.</div>

      {/* Eingabe */}
      <div className="mt-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[100px]">
          <label className="text-[9px] uppercase tracking-widest text-[#A8A398]">Sitz</label>
          <input value={seat} onChange={(e) => setSeat(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="z. B. A5"
            data-testid="seatblock-seat-input"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] uppercase" />
        </div>
        <div className="min-w-[160px]">
          <label className="text-[9px] uppercase tracking-widest text-[#A8A398]">Grund</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="seatblock-reason-select"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]">
            {REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <button type="button" onClick={add} data-testid="seatblock-add-btn"
          className="px-4 py-2 rounded bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#EEDC82]">
          Sperren
        </button>
      </div>

      {/* Liste */}
      {(blocks || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="seatblock-list">
          {(blocks || []).map((b) => {
            const meta = REASONS.find((r) => r.v === (b.reason || "").toLowerCase()) || REASONS[3];
            return (
              <span key={b.seat} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px]"
                style={{ borderColor: meta.color + "80", color: meta.color, backgroundColor: meta.color + "10" }}
                data-testid={`seatblock-chip-${b.seat}`}>
                <span className="font-mono font-bold">{b.seat}</span>
                <span className="uppercase tracking-[0.2em] text-[9px] opacity-90">{meta.l}</span>
                <button type="button" onClick={() => remove(b.seat)} className="hover:text-white ml-1">×</button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
